import { ActionFunctionArgs, LoaderFunctionArgs, json } from "@remix-run/node"
import { Private, Public } from "~/utils/remix"
import defaultAvatar from "~/assets/default-avatar.webp"
import { Users, VideoComment } from "@prisma/client"
import { AppLoadContext } from "@remix-run/server-runtime"
import Typography from "~/components/typography"
import { Link, ShouldRevalidateFunction, useFetcher } from "@remix-run/react"
import { Avatar, AvatarImage, AvatarFallback } from "~/components/ui/avatar"
import { useEffect, useState } from "react"
import showdown from "showdown"
import moment from "moment"
import { Separator } from "~/components/ui/separator"
import { FormText, SubmitButton } from "~/components/form-fields"
import { ValidatedForm, validationError } from "remix-validated-form"
import { z } from "zod"
import { withZod } from "@remix-validated-form/with-zod"
import { Button } from "~/components/ui/button"
import { ChatBubbleIcon, Pencil2Icon, ResetIcon, TrashIcon } from "@radix-ui/react-icons"
import { ConfirmDialog } from "~/components/dialogs"
import { cn } from "~/utils/shadecn"
import { getImage } from "~/utils/image.server"
import {
  Comment,
  CommentAndChildren,
  CommentAndUser,
  commentValidator,
  deleteValidator,
  updateValidator,
} from "~/components/comment"
import { Purchase } from "~/components/purchase"

const tree = (context: AppLoadContext, comments: CommentAndUser[]) => {
  const commentsByID = new Map<number, CommentAndChildren>()
  const commentsByParent = new Map<number, CommentAndChildren[]>()

  comments.forEach((comment) => {
    if (comment.UserID !== context.user?.UserID && comment.Status === 0) {
      return
    }
    if (comment.UserID === context.user?.UserID && comment.Deleted === 1 && comment.Status === 0) {
      return
    }

    const commentID = Number(comment.CommentID)
    const parentID = Number(comment.ParentCommentID) || 0

    if (!commentsByParent.has(parentID)) {
      commentsByParent.set(parentID, [])
    }

    const val = {
      ...comment,
      Children: [],
    }

    commentsByID.set(commentID, val)
    commentsByParent.get(parentID)!.push(val)
  })

  commentsByParent.forEach((comments, parentID) => {
    if (parentID === 0 || !commentsByID.get(parentID)) {
      return
    }

    commentsByID.get(parentID)!.Children = comments.sort((a, b) => {
      return b.CreatedAt.getTime() - b.CreatedAt.getTime()
    })
  })

  return commentsByParent.get(0)?.sort((a, b) => {
    if (a.PurchaseID && !b.PurchaseID) return -1
    if (!a.PurchaseID && b.PurchaseID) return 1

    return b.CreatedAt.getTime() - a.CreatedAt.getTime()
  })
}

export const action = Private(async (args: ActionFunctionArgs) => {
  const db = args.context.db
  const user = args.context.user!

  switch (args.request.method) {
    case "POST":
      const commentForm = await commentValidator.validate(await args.request.formData())
      if (commentForm.error) {
        return validationError(commentForm.error)
      }

      const video = await db.videos.findFirst({
        where: { VideoID: commentForm.data.VideoID },
      })
      if (!video) {
        throw new Response(null, {
          status: 404,
          statusText: "Video Not Found",
        })
      }

      const comment = await db.videoComment.create({
        data: {
          VideoID: commentForm.data.VideoID,
          StarID: video.UserID,
          UserID: user.UserID,
          ParentCommentID: commentForm.data.ParentCommentID,
          Content: commentForm.data.Content,
        },
      })
      break
    case "PUT":
      const updateForm = await updateValidator.validate(await args.request.formData())
      if (updateForm.error) {
        return validationError(updateForm.error)
      }

      await db.videoComment.update({
        where: {
          UserID: user.UserID,
          CommentID: updateForm.data.CommentID,
          Status: 0,
        },
        data: { Content: updateForm.data.Content },
      })
      break
    case "DELETE":
      const deleteForm = await deleteValidator.validate(await args.request.formData())
      if (deleteForm.error) {
        return validationError(deleteForm.error)
      }

      await db.videoComment.update({
        where: { UserID: user.UserID, CommentID: deleteForm.data.CommentID },
        data: { Deleted: 1 },
      })
      break
    default:
      return json({ error: "Method not allowed" }, { status: 405 })
  }

  const comments = await db.videoComment.findMany({
    where: {
      VideoID: parseInt(args.params.videoID!),
      Status: { not: 2 },
    },
    include: {
      User: {
        select: {
          UserName: true,
          DisplayName: true,
          Avatar: true,
          Private: true,
        },
      },
      Purchase: {
        include: {
          AdditionalPackage: true,
        },
      },
    },
    orderBy: { CreatedAt: "desc" },
  })

  return json({
    comments: tree(
      args.context,
      comments.map((comment) => commentResult(comment))
    ),
    revalidate: false,
  })
})

export const loader = Public(async ({ request, context, params }: LoaderFunctionArgs) => {
  const db = context.db

  if (!params.videoID) {
    return json({ error: "No video ID provided" }, { status: 400 })
  }

  const comments = await db.videoComment.findMany({
    where: {
      VideoID: parseInt(params.videoID),
      Status: { not: 2 },
    },
    include: {
      User: {
        select: {
          UserName: true,
          DisplayName: true,
          Avatar: true,
          Private: true,
        },
      },
      Purchase: {
        include: {
          AdditionalPackage: true,
        },
      },
    },
    orderBy: { CreatedAt: "desc" },
  })

  return json({
    comments: tree(
      context,
      comments.map((comment) => commentResult(comment))
    ),
  })
})

const commentResult = (
  comment: VideoComment & {
    User: {
      UserName: string | null
      DisplayName: string | null
      Avatar: string | null
      Private: number
    } | null
    Purchase?: {
      AdditionalPackage?: {
        ImageUrl: string
      } | null
    } | null
  }
) => {
  let content = comment.Content || ""
  try {
    content = unescapeHTML(decodeURI(comment.Content || ""))
  } catch (e) {}

  const IsLoveNote = comment.PurchaseID !== null
  const HasLoveObject = comment.Purchase?.AdditionalPackage?.Tag?.startsWith("object") || false
  const LoveObjectSrc = comment.Purchase?.AdditionalPackage?.ImageUrl || null

  return {
    Content: content,
    Status: comment.Status!,
    Deleted: comment.Deleted!,
    CommentID: comment.CommentID,
    ParentCommentID: comment.ParentCommentID,
    UserID: comment.UserID,
    UserPrivate: comment.User?.Private!,
    UserName: comment.User?.UserName!,
    DisplayName: comment.User?.DisplayName!,
    Avatar: getImage(comment.User?.Avatar, defaultAvatar),
    CreatedAt: comment.CreatedAt!,
    IsAnonymous: comment.IsAnonymous,
    PurchaseID: comment.PurchaseID,
    IsLoveNote,
    HasLoveObject,
    LoveObjectSrc,
    Children: [],
  }
}

export const shouldRevalidate: ShouldRevalidateFunction = (args) => {
  if (!args.actionResult?.revalidate) {
    return false
  }
  return args.defaultShouldRevalidate
}

export default function VideoComments({
  currentUser,
  currentUserAvatar,
  videoID,
  className,
  disabled,
  refresh,
  setRefresh,
}: {
  currentUser: Users
  currentUserAvatar: string
  videoID: number
  className: string
  disabled?: boolean
  refresh: boolean
  setRefresh: (state: boolean) => void
}) {
  const fetcher = useFetcher()
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(false)
  const [comments, setComments] = useState<CommentAndChildren[] | null>(null)

  useEffect(() => {
    setSubmitting(fetcher.state == "submitting")
    setLoading(fetcher.state == "loading")
    fetcher.state === "idle" && setRefresh && setRefresh(false)
  }, [fetcher.state])

  useEffect(() => {
    if (fetcher.state === "idle" && !fetcher.data) {
      fetcher.load(`/videos/${videoID}/comments`)
    }
  }, [fetcher])

  useEffect(() => {
    fetcher.load(`/videos/${videoID}/comments`)
  }, [refresh])

  return (
    <div className={className}>
      <Typography>
        <h2>Comments</h2>
      </Typography>
      <Separator className="mt-4 mb-4" />
      <div>
        {!disabled && (
          <div className="bg-primary/10 px-3 py-2 mb-2 rounded-md">
            <ValidatedForm
              className="space-y-2 mb-2"
              validator={commentValidator}
              action={`/videos/${videoID}/comments`}
              resetAfterSubmit={true}
              fetcher={fetcher}
              method="post">
              <input type="hidden" name="VideoID" value={videoID} />
              <FormText
                name="Content"
                prefix={<ChatBubbleIcon className="h-4 w-4" />}
                placeholder="Leave a comment"
                inputClasses="min-h-[4rem] h-[4rem] pr-3 pl-8 bg-background text-xs border-primary/50"
              />
              <SubmitButton size="sm" className="w-32 h-7" content="Comment" loading="Commenting..." />
            </ValidatedForm>
          </div>
        )}

        <div className="flex flex-col mt-2">
          {fetcher.data?.comments?.map((comment, index) => (
            <Comment
              key={index}
              currentUser={currentUser}
              currentUserAvatar={currentUserAvatar}
              level={0}
              videoID={videoID}
              comment={comment}
              disabled={disabled}
              fetcher={fetcher}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

var htmlEntities: any = {
  nbsp: " ",
  cent: "¢",
  pound: "£",
  yen: "¥",
  euro: "€",
  copy: "©",
  reg: "®",
  lt: "<",
  gt: ">",
  quot: '"',
  amp: "&",
  apos: "'",
}

function unescapeHTML(str: string) {
  return str.replace(/\&([^;]+);/g, function (entity, entityCode) {
    var match

    if (entityCode in htmlEntities) {
      return htmlEntities[entityCode]
    } else if ((match = entityCode.match(/^#x([\da-fA-F]+)$/))) {
      return String.fromCharCode(parseInt(match[1], 16))
    } else if ((match = entityCode.match(/^#(\d+)$/))) {
      return String.fromCharCode(~~match[1])
    } else {
      return entity
    }
  })
}
